import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Point,
  CommonHeader,
  Select,
} from "./BoardingAndDropping.styles.js";

const EMPTY_POINT = {
  time: "",
  detail: "",
  standName: "",
  stand_id: "",
};

const BoardingAndDropping = ({
  tripId,
  setBoardingInfromation,
  setDroppingInformation,
}) => {
  const { webSettingData, languageData } = useSelector(
    (state) => state.busLists
  );

  const [boardingPoint, setBoardingPoint] = useState([]);
  const [droppingPoint, setDroppingPoint] = useState([]);
  const [standList, setStandList] = useState([]);

  const [boardingId, setBoardingId] = useState("");
  const [droppingId, setDroppingId] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const languageText = (key, fallback) =>
    languageData?.[key]?.[webSettingData?.language] || fallback;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const resetSelections = () => {
      setBoardingId("");
      setDroppingId("");
      setBoardingInfromation({ ...EMPTY_POINT });
      setDroppingInformation({ ...EMPTY_POINT });
    };

    const loadPoints = async () => {
      if (!tripId) {
        setBoardingPoint([]);
        setDroppingPoint([]);
        setStandList([]);
        resetSelections();
        setLoadError("");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError("");
      resetSelections();

      try {
        const apiDomain = process.env.REACT_APP_API_MODULE_DOMAIN;

        const [boardingRes, droppingRes, standsRes] =
          await Promise.all([
            fetch(
              `${apiDomain}/triplist/boardings/${tripId}`,
              { signal: controller.signal }
            ),
            fetch(
              `${apiDomain}/triplist/droppings/${tripId}`,
              { signal: controller.signal }
            ),
            fetch(`${apiDomain}/stands`, {
              signal: controller.signal,
            }),
          ]);

        if (
          !boardingRes.ok ||
          !droppingRes.ok ||
          !standsRes.ok
        ) {
          throw new Error(
            "Unable to load boarding and dropping points."
          );
        }

        const [boardingData, droppingData, standsData] =
          await Promise.all([
            boardingRes.json(),
            droppingRes.json(),
            standsRes.json(),
          ]);

        if (cancelled) return;

        const boardingItems = Array.isArray(boardingData?.data)
          ? boardingData.data
          : [];

        const droppingItems = Array.isArray(droppingData?.data)
          ? droppingData.data
          : [];

        const stands = Array.isArray(standsData?.data)
          ? standsData.data
          : [];

        setBoardingPoint(boardingItems);
        setDroppingPoint(droppingItems);
        setStandList(stands);
      } catch (error) {
        if (cancelled || error?.name === "AbortError") return;

        console.error("Failed to load route points:", error);

        setBoardingPoint([]);
        setDroppingPoint([]);
        setStandList([]);

        setLoadError(
          error?.message ||
            "Unable to load boarding and dropping points."
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPoints();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    tripId,
    setBoardingInfromation,
    setDroppingInformation,
  ]);

  const getStandName = (standId) => {
    if (standId === undefined || standId === null || standId === "") {
      return "";
    }

    const stand = standList.find(
      (item) => String(item.id) === String(standId)
    );

    return stand?.name || "";
  };

  const formatPointLabel = (item) => {
    const standName = getStandName(item?.stand_id);

    return [
      item?.time,
      standName,
      item?.detail,
    ]
      .filter(
        (value) =>
          value !== undefined &&
          value !== null &&
          String(value).trim() !== ""
      )
      .join(" - ");
  };

  const mapPointInformation = (item) => ({
    time: item?.time || "",
    detail: item?.detail || "",
    standName: getStandName(item?.stand_id),
    stand_id: item?.stand_id ?? "",
  });

  const handleBoardingChange = (event) => {
    const selectedId = event.target.value;
    setBoardingId(selectedId);

    const item = boardingPoint.find(
      (point) => String(point.id) === String(selectedId)
    );

    setBoardingInfromation(
      item ? mapPointInformation(item) : { ...EMPTY_POINT }
    );
  };

  const handleDroppingChange = (event) => {
    const selectedId = event.target.value;
    setDroppingId(selectedId);

    const item = droppingPoint.find(
      (point) => String(point.id) === String(selectedId)
    );

    setDroppingInformation(
      item ? mapPointInformation(item) : { ...EMPTY_POINT }
    );
  };

  return (
    <Point className="boarding-dropping-container">
      <div className="boarding-dropping-fields">
        <div className="boarding-dropping-field">
          <CommonHeader className="boarding-dropping-label">
            {languageText(
              "booking_page_boarding_title",
              "Boarding Point"
            )}
            <span aria-hidden="true" className="required-mark">
              {" "}*
            </span>
          </CommonHeader>

          <Select
            className="boarding-dropping-select"
            name="boardingPoint"
            id="boardingPoint"
            value={boardingId}
            onChange={handleBoardingChange}
            required
            disabled={isLoading || boardingPoint.length === 0}
            aria-label="Select boarding point"
          >
            <option value="">
              {isLoading
                ? "Loading boarding points..."
                : "Select boarding point"}
            </option>

            {boardingPoint.map((item) => (
              <option key={item.id} value={String(item.id)}>
                {formatPointLabel(item)}
              </option>
            ))}
          </Select>
        </div>

        <div className="boarding-dropping-field">
          <CommonHeader className="boarding-dropping-label">
            {languageText(
              "booking_page_dropping_title",
              "Dropping Point"
            )}
            <span aria-hidden="true" className="required-mark">
              {" "}*
            </span>
          </CommonHeader>

          <Select
            className="boarding-dropping-select"
            name="droppingPoint"
            id="droppingPoint"
            value={droppingId}
            onChange={handleDroppingChange}
            required
            disabled={isLoading || droppingPoint.length === 0}
            aria-label="Select dropping point"
          >
            <option value="">
              {isLoading
                ? "Loading dropping points..."
                : "Select dropping point"}
            </option>

            {droppingPoint.map((item) => (
              <option key={item.id} value={String(item.id)}>
                {formatPointLabel(item)}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {loadError && (
        <p className="boarding-dropping-error" role="alert">
          {loadError}
        </p>
      )}

      {!isLoading &&
        !loadError &&
        tripId &&
        boardingPoint.length === 0 && (
          <p className="boarding-dropping-message">
            No boarding points are available for this trip.
          </p>
        )}

      {!isLoading &&
        !loadError &&
        tripId &&
        droppingPoint.length === 0 && (
          <p className="boarding-dropping-message">
            No dropping points are available for this trip.
          </p>
        )}
    </Point>
  );
};

export default BoardingAndDropping;